// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { func, bool } from 'prop-types';
import { Button } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { Package, Copy, BadgePercent, SquareArrowOutUpRight, Megaphone, Boxes, SquareStack, PackageOpen, Eye } from 'lucide-react';

import IQuote from 'Components/IQuote/IQuote';
import Img from 'Containers/Image/Image';
import Badges from 'Components/Badges/Badges';
import AddToWishlistMenu from 'Containers/Wishlists/AddToWishlistMenu';
import { renderGridStock } from 'Components/Products/helpers';
import Price from 'Components/Price/Price';
import ProductPropType from 'Types/product';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import { StyledProductGridItem } from 'Components/Products/StyledProductItems';
import AddToCart from 'Components/AddToCart/AddToCart';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';
import highlightText from 'Utils/highlightText';
import useNumberUtils from 'Hooks/useNumberUtils';
import { UserContext } from 'Contexts/UserContext';
// TODO: Remove this after Black Friday
import { blackFridayProducts } from 'Constants';

const ProductGridItem = ({
  product,
  addToCartLoading,
  onAddToCart,
  onStockAlertModalOpen,
  onAddToWishlist,
  onQuickView,
  hideQuickView
}) => {
  const { formatNumber } = useNumberUtils();
  const { t } = useTranslation();
  const theme = useTheme();
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const currency = userData?.defaultCurrency;
  const withVat = userData?.withVat;
  const accountManagerPhone = userData?.accountManager?.phone;
  const toast = useToastNotifications();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledProductGridItem theme={ theme }>
      <div className="preview">
        { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
          <Badges
            isEol={ product.isEol }
            isOnDemandOnly={
              // eslint-disable-next-line no-nested-ternary
              product.reservedStockValue > 0
                ? false
                : product.stockValue <= 0
                  ? product.isOnDemandOnly
                  : false
            }
            isNew={ product.isNew }
            isResealed={ !!product.originalCode }
            savings={ Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100 }
            isSupplierOffer={ !product?.hasPrice }
            hasSupplierOffers={ product.hasSupplierOffers }
          />
        ) }
        { product.appliedPromotions.length > 0 && (
          <div className="promotions">
            <Boxes />
            <p
              dangerouslySetInnerHTML={ {
                __html: t(`products.promotionData.type.${ Object.keys(product.promotions).at(0) }`)
              } }
            />
          </div>
        ) }
        { (!product.appliedPromotions.length > 0 && !!product?.priceScales?.length) && (
          <div className="promotions">
            <BadgePercent />
            <p>{ t('products.priceScales') }</p>
          </div>
        ) }
        { blackFridayProducts.includes(product.code) && ( // TODO: Remove this after Black Friday
          <div className="blackFriday">
            <img src={ BlackFriday } alt="Black Friday" />
          </div>
        ) }
        <Link
          to={ `/products/${product.slug}/pd/${product.id}` }
          state={ {
            component: 'product_grid_item',
            source: 'product_grid_page',
            clickedOn: 'image',
            productId: product.id
          } }
        >
          <Img
            className={ `image ${product.pictures.length === 0 ? 'placeholder' : ''}` }
            src={ product?.pictures?.at(0)?.urlThumbnailPicture }
            unloader={ <Package size={ 100 } strokeWidth={ 1.25 } /> }
            loading="lazy"
            alt={ product.name }
          />
        </Link>
      </div>
      <div className="helperButtons">
        { !hideQuickView && (
          <Button
            onClick={ onQuickView }
            kind="ghost"
            data-test="onQuickView"
            data-testid={ product.productId }
            className="quickView"
          >
            <Eye /> { t('products.quickView') }
          </Button>
        ) }
        { Boolean(product.inCsp) && (
          <div className="csp">
            <SquareArrowOutUpRight />
            <a href="https://bcp.nod.ro/login" target="_blank">{ t('products.continueCsp') }</a>
          </div>
        ) }
        {
          Boolean(product.inIquote) && !product.appliedPromotions.length && (
            <div className="helperButtons">
              <IQuote product={ product } />
            </div>
          )
        }
        { Boolean(product.hasResealed) && (
          <div className="resealed">
            <PackageOpen /> { product?.resealedCount || '' } { t('products.resealed') }
          </div>
        ) }
      </div>
      <div className="details">
        <div className="top">
          <Link
            className="title"
            to={ `/products/${ product.slug }/pd/${product.id}` }
            state={ {
              component: 'product_grid_item',
              source: 'product_grid_page',
              clickedOn: 'title',
              productId: product.id
            } }
          >
            { highlightText(product.title, product?.highlighted?.title) }
          </Link>
          <p className="code">
            <span className="codeLabel">{ t('products.code') }:</span>
            <span className="codeValue" title={ product.code }>{ product.code }</span>
            <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } />
          </p>
          { !product?.warnings?.blocked_stocks && (
            renderGridStock({ t, product, info: { accountManagerPhone } })
          ) }
          { (!product?.hasPrice || product?.hasSupplierOffers) && (
            <DeliveryEstimate
              estimate={ product?.supplierOfferDeliveryTimeDays }
              isExternalSupplier={ !product?.hasPrice }
            />
          ) }
        </div>
        { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
          <Badges
            specialPriceValidTo={ product.specialPriceValidTo }
            specialPrice={
              product.specialPrice && !product.isResealed
                ? (product.price !== product.promoPrice && ((product.price - product.promoPrice) / product.price) >= 0.01)
                : false
            }
            fixedPrice={ product.fixedPrice }
            isClearance={ product.stockClearingPrice }
          />
        ) }
      </div>
      { (!product?.hasPriceRequest && !product?.warnings?.blocked_prices) && (
        <Price
          price={ product.price }
          promoPrice={ product.promoPrice }
          withTooltip
          withVat={ withVat }
          displayVatInfo
          tooltipContent={ `
            ${ t('products.greenStamp') }
            ${ formatNumber(product.greenStampValue, { currency }) }
          ` }
        />
      ) }
      { product.minQuantity > 1 && (
        <p className="minQuantity">
          <SquareStack />
          { t('products.multipleOf', { multiple: product.minQuantity }) }
        </p>
      ) }
      <div className="buttonActions">
        { /* eslint-disable no-nested-ternary */ }
        { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
          ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
            ? (
              <Button
                className="red stockAlert"
                kind="ghost"
                onClick={ onStockAlertModalOpen }
                data-test="onStockAlert"
                data-testid={ product.productId }
              >
                <Megaphone />
                { t('products.stockAlert') }
              </Button>
            )
            : (
              <AddToCart
                onClick={ onAddToCart }
                trackingContext={ {
                  location: 'product_grid_item',
                  product_sku: product?.code,
                } }
                showSelector={ false }
                loading={ addToCartLoading }
                disabled={
                  addToCartLoading
                  || product.warnings?.blocked_prices
                  || product.warnings?.blocked_stocks
                  || product.warnings?.blocked_orders
                }
                min={ Math.max(product.minQuantity, 1) }
                step={ Math.max(product.minQuantity, 1) }
                max={ product.stockValue }
                value={ product.quantity }
                id={ product.id }
                size="md"
              />
            )
          )}
        <AddToWishlistMenu
          onAddToWishlist={ onAddToWishlist }
          canCreate
          product={ product }
        />
      </div>
    </StyledProductGridItem>
  );
};

ProductGridItem.propTypes = {
  product: ProductPropType,
  addToCartLoading: bool,
  onAddToCart: func,
  onQuickView: func,
  hideQuickView: bool,
  onAddToWishlist: func,
  onStockAlertModalOpen: func,
};

export default ProductGridItem;
